import { types, type Instance, getParent, flow } from 'mobx-state-tree';
import { StarRating } from 'src/components/type';
import { ACTION_DONE, ACTION_ERROR } from '../state';
import { logError } from 'src/utils/logError';
import type { IJobApplication } from './JobApplication';
import { trackChangeRating, trackClearedRating } from 'src/utils/events';
import { sendHotjarTag } from 'src/utils/hotjar';
import { HOTJAR_TAG_RATING } from 'src/constants/hotjar';
import { TRACKING_CANDIDATE_DETAILS } from 'src/constants/pageTrackingData';
import {
  type UpdateApplicationRatingResponse,
  updateApplicationRating,
} from 'src/graphql/hirer/updateApplicationRating';
export const Rating = types
  .model('Rating', {
    rating: types.optional(types.number, StarRating.ZERO),
  })
  .views((self) => ({
    get jobApplication(): IJobApplication {
      return getParent(self) as IJobApplication;
    },

    get trackingAttributes(): { candidateId: string; rating: StarRating } {
      const rating = self.rating;
      return {
        ...(this.jobApplication as IJobApplication).baseTrackingAttributes,
        rating,
      };
    },
  }))
  .actions((self) => {
    const updateRating: any = flow(function* (
      rating: StarRating,
      currentPage = TRACKING_CANDIDATE_DETAILS,
    ) {
      const initRating = self.rating;
      try {
        self.rating = rating;
        const updateApplicationRatingResult: UpdateApplicationRatingResponse =
          yield updateApplicationRating(
            (self.jobApplication as IJobApplication).id,
            rating,
          );

        const trackingAttributes = {
          ...self.trackingAttributes,
          currentPage,
          prospectId: self.jobApplication.id,
        };
        if (updateApplicationRatingResult) {
          self.rating = updateApplicationRatingResult.rating;
          if (self.rating === StarRating.ZERO) {
            trackClearedRating(trackingAttributes);
          } else {
            trackChangeRating(trackingAttributes);
          }
        }
        sendHotjarTag(HOTJAR_TAG_RATING);
        return ACTION_DONE;
      } catch (error: any) {
        self.rating = initRating;
        logError(
          {
            errorCode: 'UPDATE_APPLICATION_RATING',
            description: 'Error attempting: updateApplicationRating',
          },
          {
            error,
            rating,
            currentPage,
            prospectId: self.jobApplication.id,
          },
        );
        return ACTION_ERROR;
      }
    });

    return { updateRating };
  });

export interface IRating extends Instance<typeof Rating> {}

export default Rating;
